import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, X, MapPin } from 'lucide-react';
import { useConflits } from '../../hooks/useConflits';
import AdminLayout from '../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: var(--text-dark);
  font-weight: 700;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-blue);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: #004d8f;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: var(--off-white);
  font-weight: 600;
  color: var(--text-dark);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--border-light);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'active': return '#FEE2E2';
      case 'mediation': return '#FEF3C7';
      case 'resolved': return '#D1FAE5';
      default: return '#F3F4F6';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'active': return '#DC2626';
      case 'mediation': return '#D97706';
      case 'resolved': return '#059669';
      default: return '#6B7280';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: var(--transition);
  margin-right: 0.5rem;

  &:hover {
    background: var(--background-light);
  }

  &.delete:hover {
    background: #FEE2E2;
    color: #DC2626;
  }
`;

const MapPreview = styled.div`
  width: 100%;
  height: 200px;
  background: #f0f4f8;
  border-radius: var(--radius-md);
  position: relative;
  margin-top: 0.5rem;
  cursor: crosshair;
  overflow: hidden;
  border: 2px dashed var(--border-light);
`;

const MapPreviewLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-light);
  font-size: 0.85rem;
  text-align: center;
  pointer-events: none;
`;

const ClickMarker = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  background: ${props => props.$color || '#E74C3C'};
  border: 2px solid white;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-dark);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--primary-blue);
  color: white;
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: #004d8f;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--background-light);
  color: var(--text-dark);
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: var(--border-light);
  }
`;

const statusLabels = {
  active: 'Conflit actif',
  mediation: 'En médiation',
  resolved: 'Résolu'
};

// Coordonnées prédéfinies des villes du Tchad sur la carte
const villesCoords = {
  "N'Djamena": { x: 28, y: 52 },
  "Moundou": { x: 45, y: 82 },
  "Abéché": { x: 82, y: 45 },
  "Sarh": { x: 58, y: 62 },
  "Faya": { x: 58, y: 15 },
  "Ati": { x: 66, y: 38 },
  "Bol": { x: 16, y: 32 },
  "Adré": { x: 84, y: 58 },
  "Mongo": { x: 52, y: 40 },
  "Doba": { x: 48, y: 72 },
  "Massakory": { x: 30, y: 42 },
  "Am Timan": { x: 65, y: 55 },
  "Bongor": { x: 34, y: 68 },
  "Pala": { x: 26, y: 72 },
  "Koumra": { x: 58, y: 70 },
  "Mao": { x: 18, y: 18 },
  "Zouar": { x: 38, y: 8 },
  "Fada": { x: 82, y: 18 },
  "Goz Beïda": { x: 84, y: 52 },
  "Biltine": { x: 74, y: 35 },
  "Bardai": { x: 42, y: 8 },
  "Faya-Largeau": { x: 58, y: 15 },
  "Moussoro": { x: 34, y: 28 }
};

const AdminConflits = () => {
  const { conflits, loading, createConflit, editConflit, removeConflit } = useConflits();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConflit, setEditingConflit] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    x: '',
    y: '',
    status: 'active',
    color: '#E74C3C'
  });
  const [selectedVille, setSelectedVille] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      x: parseFloat(formData.x),
      y: parseFloat(formData.y)
    };

    if (editingConflit) {
      await editConflit(editingConflit.id, data);
    } else {
      await createConflit(data);
    }
    closeModal();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce conflit ')) {
      await removeConflit(id);
    }
  };

  const openModal = (conflit = null) => {
    if (conflit) {
      setEditingConflit(conflit);
      setSelectedVille(conflit.label in villesCoords ? conflit.label : 'autre');
      setFormData({
        label: conflit.label,
        x: conflit.x,
        y: conflit.y,
        status: conflit.status,
        color: conflit.color
      });
    } else {
      setEditingConflit(null);
      setSelectedVille('');
      setFormData({
        label: '',
        x: '',
        y: '',
        status: 'active',
        color: '#E74C3C'
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingConflit(null);
    setSelectedVille('');
  };

  const handleStatusChange = (status) => {
    const colors = {
      active: '#E74C3C',
      mediation: '#F39C12',
      resolved: '#27AE60'
    };
    setFormData({ ...formData, status, color: colors[status] });
  };

  const handleVilleChange = (ville) => {
    setSelectedVille(ville);
    if (villesCoords[ville]) {
      setFormData({
        ...formData,
        label: ville,
        x: villesCoords[ville].x,
        y: villesCoords[ville].y
      });
    } else if (ville === 'autre') {
      setFormData({ ...formData, label: '', x: '', y: '' });
    }
  };

  const handleMapClick = (e) => {
    if (selectedVille !== 'autre') return; // Seulement en mode manuel
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setFormData({
      ...formData,
      x: parseFloat(x.toFixed(1)),
      y: parseFloat(y.toFixed(1))
    });
  };

  return (
    <AdminLayout>
      <Container>
        <Header>
          <Title>Gestion des Conflits</Title>
          <AddButton onClick={() => openModal()}>
            <Plus size={20} />
            Ajouter un conflit
          </AddButton>
        </Header>

        <Table>
          <thead>
            <tr>
              <Th>Ville/Région</Th>
              <Th>Position X</Th>
              <Th>Position Y</Th>
              <Th>Statut</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center' }}>Chargement...</Td>
              </tr>
            ) : conflits.length === 0 ? (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center' }}>Aucun conflit enregistré</Td>
              </tr>
            ) : (
              conflits.map((conflit) => (
                <tr key={conflit.id}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <MapPin size={16} color={conflit.color} />
                      {conflit.label}
                    </div>
                  </Td>
                  <Td>{conflit.x}%</Td>
                  <Td>{conflit.y}%</Td>
                  <Td>
                    <StatusBadge $status={conflit.status}>
                      {statusLabels[conflit.status]}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButton onClick={() => openModal(conflit)}>
                      <Edit2 size={18} />
                    </ActionButton>
                    <ActionButton className="delete" onClick={() => handleDelete(conflit.id)}>
                      <Trash2 size={18} />
                    </ActionButton>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {isModalOpen && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingConflit ? 'Modifier' : 'Ajouter'} un conflit</h2>
                <ActionButton onClick={closeModal}>
                  <X size={24} />
                </ActionButton>
              </ModalHeader>

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Ville / Région</Label>
                  <Select
                    value={selectedVille}
                    onChange={(e) => handleVilleChange(e.target.value)}
                    required
                  >
                    <option value="">-- Sélectionnez une ville --</option>
                    {Object.keys(villesCoords).sort().map(ville => (
                      <option key={ville} value={ville}>{ville}</option>
                    ))}
                    <option value="autre">-- Autre (saisie manuelle) --</option>
                  </Select>
                </FormGroup>

                {selectedVille === 'autre' && (
                  <FormGroup>
                    <Label>Nom de la localité</Label>
                    <Input
                      type="text"
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      placeholder="Ex: Village de..."
                      required
                    />
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>Position X (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.x}
                    onChange={(e) => setFormData({ ...formData, x: e.target.value })}
                    placeholder="0-100"
                    required
                    readOnly={selectedVille !== 'autre'}
                    style={{ backgroundColor: selectedVille !== 'autre' ? '#f3f4f6' : 'white' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Position Y (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.y}
                    onChange={(e) => setFormData({ ...formData, y: e.target.value })}
                    placeholder="0-100"
                    required
                    readOnly={selectedVille !== 'autre'}
                    style={{ backgroundColor: selectedVille !== 'autre' ? '#f3f4f6' : 'white' }}
                  />
                </FormGroup>

                {selectedVille === 'autre' && (
                  <FormGroup>
                    <Label>Cliquez sur la carte pour positionner</Label>
                    <MapPreview onClick={handleMapClick}>
                      <MapPreviewLabel>
                        Cliquez pour placer le marqueur<br/>
                        <small>X: {formData.x || '-'} | Y: {formData.y || '-'}</small>
                      </MapPreviewLabel>
                      {formData.x && formData.y && (
                        <ClickMarker 
                          $color={formData.color}
                          style={{ 
                            left: `${formData.x}%`, 
                            top: `${formData.y}%` 
                          }} 
                        />
                      )}
                    </MapPreview>
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>Statut</Label>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <option value="active">Conflit actif</option>
                    <option value="mediation">En médiation</option>
                    <option value="resolved">Résolu</option>
                  </Select>
                </FormGroup>

                <ButtonGroup>
                  <CancelButton type="button" onClick={closeModal}>
                    Annuler
                  </CancelButton>
                  <SubmitButton type="submit">
                    {editingConflit ? 'Modifier' : 'Ajouter'}
                  </SubmitButton>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminConflits;
